<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            ajax-path="cachet/clients"
        />
        <app-modal
            :show.sync="showIncidentsModal"
            width="lg:w-2/3"
        >
            <template slot="header">{{ selectedClient.url }} Incidents</template>
            <template slot="body">
                <tabulator
                    :columns="incidentsColumns"
                    :actions="incidentsActions"
                    :refresh.sync="refreshIncidentsTable"
                    :ajax-path="`cachet/clients/${selectedClient.id}/incidents`"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddClientModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Add Client</template>
            <template slot="body">
                <app-input
                    type="text"
                    v-model="addClientForm.url"
                    placeholder="URL"
                    :error="addClientForm.errors.get('url')"
                    @updated="addClientForm.errors.clear('url')"
                    required
                    focus
                />                
                <app-input 
                    type="text"
                    v-model="addClientForm.token"
                    placeholder="Token"
                    :error="addClientForm.errors.get('token')"
                    @updated="addClientForm.errors.clear('token')"
                    required
                />
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddClientModal = false"
                        :disable="addingClient"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="addClient"
                        :loading="addingClient"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditClientModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Edit Client</template>
            <template slot="body">
                <app-input
                    type="text"
                    v-model="editClientForm.url"
                    placeholder="URL"
                    :error="editClientForm.errors.get('url')"
                    @updated="editClientForm.errors.clear('url')"
                    required
                    focus
                />                
                <app-input 
                    type="text"
                    v-model="editClientForm.token"
                    placeholder="Token"
                    :error="editClientForm.errors.get('token')"
                    @updated="editClientForm.errors.clear('token')"
                    required
                />
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditClientModal = false"
                        :disable="updatingClient"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="updateClient"
                        :loading="updatingClient"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddIncidentModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Report Incident</template>
            <template slot="body">
                <app-input
                    type="text"
                    v-model="addIncidentForm.name"
                    placeholder="Name"
                    :error="addIncidentForm.errors.get('name')"
                    @updated="addIncidentForm.errors.clear('name')"
                    required
                    focus
                />                
                <app-select
                    v-model="addIncidentForm.status"
                    placeholder="Status"
                    :error="addIncidentForm.errors.get('status')"
                    @updated="addIncidentForm.errors.clear('status')"
                    :options="[
                        {id: 0, label: 'Scheduled'},
                        {id: 1, label: 'Investigating'},
                        {id: 2, label: 'Identified'},
                        {id: 3, label: 'Watching'},
                        {id: 4, label: 'Fixed'}
                    ]"
                    label-key="label"
                    value-key="id"
                    required
                />
                <app-select
                    v-model="addIncidentForm.visible"
                    placeholder="Incident Visibility"
                    :error="addIncidentForm.errors.get('visible')"
                    @updated="addIncidentForm.errors.clear('visible')"
                    :options="[
                        {id: 1, label: 'Viewable by public'},
                        {id: 0, label: 'Only visible to logged in users'},
                    ]"
                    label-key="label"
                    value-key="id"
                    required
                />
                <app-textarea
                    v-model="addIncidentForm.message"
                    placeholder="Message"
                    :error="addIncidentForm.errors.get('message')"
                    @updated="addIncidentForm.errors.clear('message')"
                    required
                />
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddIncidentModal = false"
                        :disable="addingIncident"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="addIncident"
                        :loading="addingIncident"
                    >Report</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';
    
    export default {
        data() {
            return {
                refreshTable: false,
                refreshIncidentsTable: false,
                showAddClientModal: false,
                showEditClientModal: false,
                showIncidentsModal: false,
                showAddIncidentModal: false,
                addingClient: false,
                updatingClient: false,
                selectedClient: {},
                addingIncident: false,
                addClientForm: new Form({
                    url: null,
                    token: null,
                }),
                editClientForm: new Form({
                    url: null,
                    token: null,
                }),
                addIncidentForm: new Form({
                    name: null,
                    message: null,
                    status: null,
                    visible: null,
                }),
                columns: [
                    {
                        title: 'URL',
                        field: 'url',
                        sorter: 'string',
                        minWidth: 250,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        headerSort: false,
                        minWidth: 100,
                        formatter({_cell}) {
                            if (!_cell.row.data.groups) {
                                return 'Invalid URL';
                            }
                            
                            const groups = _cell.row.data.groups.data;
                            const total = groups.length;
                            let html = `<div style="white-space:normal;width:100%;">`;

                            for (let i = 0; i < total; i++) {
                                if (groups[i].enabled_components.length) {
                                    const components = groups[i].enabled_components;

                                    html += `<div><div>${groups[i].name}</div><table style="margin-bottom: 10px;margin-left:20px;" width="100%" class="text-xs">`;
                                    
                                    for (let j = 0; j < components.length; j++) {
                                        html += `<tr><td width="250" style="color:#718096;">${components[j].name}</td>`;

                                        if (components[j].status_name == 'Operational') {
                                            html += `<td><span style="color:#38A169;padding: 3px 5px;">${components[j].status_name}</span></td>`
                                        } else {
                                            html += `<td class="text-red-500">${components[j].status_name}</td>`
                                        }

                                        html += `</tr>`;
                                    }

                                    html += `</table></div>`;
                                }
                            }

                            return `${html}</div>`;
                        }
                    }
                ],
                actions: [
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showAddClientModal = true;
                            this.addClientForm.reset();
                            this.addClientForm.errors.clear();
                        },
                    },
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        requiredSelect: true,
                        handler: selected => {
                            this.selectedClient = selected[0];
                            this.showEditClientModal = true;
                            this.editClientForm.set({
                                url: this.selectedClient.url,
                                token: this.selectedClient.token,
                            });
                            this.editClientForm.errors.clear();
                        }
                    },
                    {
                        label: 'Incidents',
                        icon: () => import('@/icons/ReportOutlined.vue'),
                        requiredSelect: true,
                        handler: selected => {
                            this.selectedClient = selected[0];
                            this.showIncidentsModal = true;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: clients => {
                            this.deleteClient(clients[0]);
                        }
                    },                    
                ],
                incidentsColumns: [],
                incidentsActions: [
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showAddIncidentModal = true;
                            this.addIncidentForm.reset();
                            this.addIncidentForm.errors.clear();
                        },
                    },
                ],
            }
        },
        methods: {
            addClient() {
                this.addClientForm.errors.clear();
                this.addingClient = true;
                Http.post('cachet/clients', this.addClientForm.payload())
                    .then(() => {
                        this.refreshTable = true;
                        this.addingClient = false;
                        this.showAddClientModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success',
                            text: 'Client was successfully added.'
                        })
                    })
                    .catch(({response}) => {
                        this.addingClient = false;
                        this.addClientForm.errors.record(response.data);
                    })
            },
            updateClient() {
                this.editClientForm.errors.clear();
                this.updatingClient = true;
                Http.patch(`cachet/clients/${this.selectedClient.id}`, this.editClientForm.payload())
                    .then(() => {
                        this.refreshTable = true;
                        this.updatingClient = false;
                        this.showEditClientModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success',
                            text: 'Client was successfully updated.'
                        })
                    })
                    .catch(({response}) => {
                        this.updatingClient = false;
                        this.editClientForm.errors.record(response.data);
                    })
            },
            deleteClient(selected) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${selected.url}</strong>" permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.delete(`cachet/clients/${selected.id}`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: `${selected.url} was successfully deleted.`
                        })
                    }
                });
            },
            addIncident() {
                this.addingIncident = true;
                this.addIncidentForm.errors.clear();
                Http.post(`cachet/clients/${this.selectedClient.id}/incidents`, this.addIncidentForm.payload())
                    .then(() => {
                        this.addingIncident = false;
                        this.showAddIncidentModal = false;
                        this.refreshIncidentsTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Incident was successfully reported.'
                        });
                    })
                    .catch(({ response }) => {
                        this.addingIncident = false;
                        this.addingIncident.errors.record(response.data);
                    })
            }
        }
    }
</script>